import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../api";

// Programmatic single-turn mic recorder for the voice-first interview. The proven
// capture pipeline (getUserMedia + MediaRecorder(webm/opus) + AnalyserNode +
// transcribeAudio) is lifted from VoiceTranscription, which stays untouched
// (SurveyPreview depends on it). What's new here: imperative start()/stop()/cancel()
// so the interview controller can open the mic on the respondent's turn, plus a
// client-side VAD.
//
// A take ends ONLY on the respondent's Submit-answer (stop) button, or after
// SILENCE_MS of continuous silence — long enough to pause and think mid-answer
// without being cut off. Any sound resets the silence window. MAX_DURATION_MS is a
// far backstop for a mic left open by accident, not a turn-ending rule.

const MIN_DURATION_MS = 700; // ignore takes too short to hold speech
export const MAX_DURATION_MS = 5 * 60 * 1000; // backstop: a stuck-open mic can't run forever
export const SILENCE_MS = 10000; // stop after this much CONTINUOUS silence
const SILENCE_RMS = 0.02; // below this = silence
const FRAME_MS = 100; // VAD sampling cadence

const pickMimeType = () => {
    if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
    for (const c of ["audio/webm;codecs=opus", "audio/webm"]) {
        if (MediaRecorder.isTypeSupported(c)) return c;
    }
    return "";
};

/**
 * @param {object}   opts
 * @param {string}   opts.surveyId
 * @param {string}   opts.token
 * @param {(blob:Blob)=>Promise<{text:string}>} [opts.transcribeAudio]  overrides the
 *        public transcribe route (the builder's preview has no respondent token)
 * @param {(t:string)=>void} opts.onResult  final (non-empty) transcript
 * @param {(kind:string,msg?:string)=>void} opts.onError  'unsupported'|'permission'|'empty'|'failed'
 * @returns {{ start:()=>void, stop:()=>void, cancel:()=>void, status:string, micLevelRef:React.MutableRefObject<number> }}
 */
export default function useMicRecorder({ surveyId, token, transcribeAudio, onResult, onError }) {
    const transcribeAudioRef = useRef(transcribeAudio);
    transcribeAudioRef.current = transcribeAudio;
    // "idle" | "recording" | "transcribing"
    const [status, setStatus] = useState("idle");

    // Keep the latest callbacks in refs so the stable start/stop identities never
    // fire a stale closure.
    const onResultRef = useRef(onResult);
    const onErrorRef = useRef(onError);
    onResultRef.current = onResult;
    onErrorRef.current = onError;

    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const startTimeRef = useRef(0);

    // VAD / analyser
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(null);
    const lastFrameRef = useRef(0);
    const silenceStartRef = useRef(null);

    // Discard flag: cancel() sets it so onstop skips transcription.
    const discardRef = useRef(false);
    // Live mic amplitude 0..1 — read by consumers in their own rAF (no re-render).
    const micLevelRef = useRef(0);

    const stopVadLoop = useCallback(() => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, []);

    const stopStreamTracks = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    }, []);

    const closeAudioCtx = useCallback(() => {
        analyserRef.current = null;
        if (audioCtxRef.current) {
            try {
                audioCtxRef.current.close();
            } catch (e) {
                /* no-op */
            }
            audioCtxRef.current = null;
        }
    }, []);

    // Stop the recorder if it's running; onstop does the rest. `discard` skips
    // transcription (used by cancel()/teardown).
    const stopInternal = useCallback((discard) => {
        discardRef.current = !!discard;
        const rec = recorderRef.current;
        if (rec && rec.state !== "inactive") {
            try {
                rec.stop();
            } catch (e) {
                /* no-op */
            }
        }
    }, []);

    const stop = useCallback(() => stopInternal(false), [stopInternal]);
    const cancel = useCallback(() => stopInternal(true), [stopInternal]);

    const transcribe = useCallback(
        async (blob) => {
            setStatus("transcribing");
            try {
                const data = transcribeAudioRef.current
                    ? await transcribeAudioRef.current(blob)
                    : await api.transcribeAudio(blob, surveyId, token);
                const text =
                    data?.text ?? data?.transcript ?? data?.data?.text ?? data?.data?.transcript ?? "";
                if (typeof text === "string" && text.trim()) {
                    onResultRef.current?.(text.trim());
                } else {
                    onErrorRef.current?.("empty");
                }
            } catch (e) {
                onErrorRef.current?.("failed", e?.message);
            } finally {
                chunksRef.current = [];
                setStatus("idle");
            }
        },
        [surveyId, token]
    );

    // VAD sampling loop (time-domain RMS). Ends the take after SILENCE_MS of
    // unbroken silence; any frame above SILENCE_RMS restarts the window.
    const vadTick = useCallback(() => {
        const analyser = analyserRef.current;
        if (!analyser) return;
        const buf = new Uint8Array(analyser.fftSize);

        const loop = () => {
            const a = analyserRef.current;
            if (!a) return;
            const now = Date.now();
            if (now - lastFrameRef.current >= FRAME_MS) {
                lastFrameRef.current = now;
                a.getByteTimeDomainData(buf);
                let sumSq = 0;
                for (let i = 0; i < buf.length; i++) {
                    const v = (buf[i] - 128) / 128;
                    sumSq += v * v;
                }
                const rms = Math.sqrt(sumSq / buf.length);
                micLevelRef.current = Math.min(1, rms * 4);

                if (rms < SILENCE_RMS) {
                    if (silenceStartRef.current == null) silenceStartRef.current = now;
                    else if (now - silenceStartRef.current >= SILENCE_MS) {
                        stopInternal(false); // auto-advance the turn
                        return;
                    }
                } else {
                    silenceStartRef.current = null;
                }
                if (now - startTimeRef.current >= MAX_DURATION_MS) {
                    stopInternal(false);
                    return;
                }
            }
            rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
    }, [stopInternal]);

    const start = useCallback(async () => {
        if (
            !navigator.mediaDevices ||
            !navigator.mediaDevices.getUserMedia ||
            typeof MediaRecorder === "undefined"
        ) {
            onErrorRef.current?.("unsupported");
            return;
        }

        let stream;
        try {
            // AGC off keeps silence quiet so the VAD threshold is meaningful;
            // echoCancellation reduces AI-audio bleed (we're also half-duplex).
            stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false },
            });
        } catch (e) {
            onErrorRef.current?.("permission");
            return;
        }

        streamRef.current = stream;
        chunksRef.current = [];
        discardRef.current = false;
        silenceStartRef.current = null;
        lastFrameRef.current = 0;
        micLevelRef.current = 0;

        // Analyser for VAD (optional — recording still works without Web Audio).
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx) {
                const ctx = new AudioCtx();
                const source = ctx.createMediaStreamSource(stream);
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 2048;
                analyser.smoothingTimeConstant = 0.6;
                source.connect(analyser); // not connected to destination — no echo
                audioCtxRef.current = ctx;
                analyserRef.current = analyser;
                vadTick();
            }
        } catch (e) {
            /* no-op — VAD optional; Done button still ends the turn */
        }

        const mimeType = pickMimeType();
        let recorder;
        try {
            recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        } catch (e) {
            stopStreamTracks();
            stopVadLoop();
            closeAudioCtx();
            onErrorRef.current?.("unsupported");
            return;
        }
        recorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
        };

        recorder.onstop = () => {
            stopVadLoop();
            closeAudioCtx();
            stopStreamTracks();
            micLevelRef.current = 0;

            const duration = Date.now() - startTimeRef.current;
            const blob = new Blob(chunksRef.current, { type: "audio/webm" });
            chunksRef.current = [];

            if (discardRef.current) {
                setStatus("idle");
                return;
            }
            if (!blob.size || duration < MIN_DURATION_MS) {
                setStatus("idle");
                onErrorRef.current?.("empty");
                return;
            }
            transcribe(blob);
        };

        startTimeRef.current = Date.now();
        recorder.start();
        setStatus("recording");
    }, [vadTick, stopStreamTracks, stopVadLoop, closeAudioCtx, transcribe]);

    // Pause the current turn: pause the recorder (where supported) and freeze the
    // VAD loop so the silence timer doesn't run while paused.
    const pause = useCallback(() => {
        const rec = recorderRef.current;
        if (rec && rec.state === "recording" && typeof rec.pause === "function") {
            try {
                rec.pause();
            } catch (e) {
                /* no-op */
            }
        }
        stopVadLoop();
    }, [stopVadLoop]);

    // Resume: continue recording and restart the VAD with a fresh silence window
    // (so the paused interval isn't counted as silence).
    const resume = useCallback(() => {
        const rec = recorderRef.current;
        if (rec && rec.state === "paused" && typeof rec.resume === "function") {
            try {
                rec.resume();
            } catch (e) {
                /* no-op */
            }
        }
        silenceStartRef.current = null;
        if (analyserRef.current && !rafRef.current) vadTick();
    }, [vadTick]);

    // Full teardown on unmount — no lingering mic indicator, context, loop or blob.
    useEffect(() => {
        return () => {
            discardRef.current = true;
            try {
                if (recorderRef.current && recorderRef.current.state !== "inactive") {
                    recorderRef.current.stop();
                }
            } catch (e) {
                /* no-op */
            }
            stopVadLoop();
            closeAudioCtx();
            stopStreamTracks();
        };
    }, [stopVadLoop, closeAudioCtx, stopStreamTracks]);

    // analyserRef exposes the live mic AnalyserNode for a voice-wave meter (null
    // when not recording).
    return { start, stop, cancel, pause, resume, status, micLevelRef, analyserRef };
}
